<script setup>
import { ref, computed } from 'vue';
import AiAgentContentCreatorAvatar from "../../agents/content-creator/avatar.vue";

const props = defineProps({
    createUrl: { type: String, required: true },
    editorBaseUrl: { type: String, required: true },
    deleteBaseUrl: { type: String, required: true },
    toggleStatusBaseUrl: { type: String, required: true },
    historicalPostsUrl: { type: String, required: true },
    historicalPostsStoreUrl: { type: String, required: true },
    initialPieces: { type: Array, default: () => [] },
});

const pieces = ref([...props.initialPieces]);
const activeFilter = ref('all');
const statusFilter = ref('all');
const showHistoricalPosts = ref(false);

const contentTypeLabels = {
    linkedin: 'LinkedIn',
    blog: 'Blog',
    instagram: 'Instagram',
    newsletter: 'Newsletter',
    facebook: 'Facebook',
    other: 'Other',
};

const contentTypeColors = {
    linkedin: 'bg-blue-100 text-blue-700',
    blog: 'bg-green-100 text-green-700',
    instagram: 'bg-pink-100 text-pink-700',
    newsletter: 'bg-brandPrimary-100 text-brandPrimary-700',
    facebook: 'bg-indigo-100 text-indigo-700',
    other: 'bg-gray-100 text-gray-700',
};

const statusOptions = [
    { name: 'all', label: _mft('content-creator:list.all') },
    { name: 'draft', label: _mft('content-creator:list.drafts') },
    { name: 'published', label: _mft('content-creator:list.published') },
];

const filters = computed(() => {
    const types = [...new Set(pieces.value.map(p => p.content_type))];
    return ['all', ...types];
});

const contentTypeOptions = computed(() => filters.value.map(filter => ({
    name: filter,
    label: filter === 'all' ? _mft('content-creator:list.all') : (contentTypeLabels[filter] || filter),
})));

const filteredPieces = computed(() => {
    let result = pieces.value;
    if (statusFilter.value !== 'all') {
        result = result.filter(p => (p.status || 'draft') === statusFilter.value);
    }
    if (activeFilter.value !== 'all') {
        result = result.filter(p => p.content_type === activeFilter.value);
    }
    return result;
});

const truncate = (text, len = 150) => {
    if (!text) return '';
    const clean = text.replace(/[#*_]/g, '');
    return clean.length > len ? clean.substring(0, len) + '...' : clean;
};

const deletePiece = async (piece) => {
    if (!confirm(_mft('content-creator:list.confirmDelete'))) return;
    try {
        await fetch(`${props.deleteBaseUrl}/${piece.id}`, {
            method: 'DELETE',
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
        });
        pieces.value = pieces.value.filter(p => p.id !== piece.id);
    } catch (e) {
        console.error('Delete failed', e);
    }
};

const toggleStatus = async (piece) => {
    try {
        const res = await fetch(`${props.toggleStatusBaseUrl}/${piece.id}/toggle-status`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
        });
        const data = await res.json();
        if (data.success) {
            piece.status = data.status;
        }
    } catch (e) {
        console.error('Toggle status failed', e);
    }
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
};

const setStatusFilter = (option) => {
    statusFilter.value = option.name;
};

const setContentTypeFilter = (option) => {
    activeFilter.value = option.name;
};
</script>

<template>
    <div class="max-w-5xl mx-auto px-4 py-8">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h1 class="text-2xl font-bold text-gray-800">
                    {{ _mft('content-creator:list.title') }}
                </h1>
                <p class="text-sm text-gray-500 mt-1">
                    {{ _mft('content-creator:list.subtitle') }}
                </p>
            </div>
            <div class="flex items-center gap-2">
                <button-base
                    @click="showHistoricalPosts = true"
                    style-type="secondary"
                    size="md"
                    class="gap-2"
                >
                    <template #icon>
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                            <polyline points="14,2 14,8 20,8" />
                            <line x1="8" y1="13" x2="16" y2="13" />
                            <line x1="8" y1="17" x2="12" y2="17" />
                        </svg>
                    </template>
                    {{ _mft('content-creator:list.writingSamples') }}
                </button-base>
                <a
                    :href="createUrl"
                >
                    <button-base
                        style-type="primary"
                    >
                        <template #icon>
                            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M12 5v14M5 12h14" />
                            </svg>
                        </template>
                        {{ _mft('content-creator:list.createNew') }}
                    </button-base>
                </a>
            </div>
        </div>

        <div class="lg:flex lg:justify-between lg:space-x-4">
            <!-- Status filters -->
            <div class="w-full mb-4 max-w-sm">
                <button-bar
                    :options="statusOptions"
                    :selected-option-name="statusFilter"
                    @select="setStatusFilter"
                    :responsive="true"
                    size="xs"
                    class="!mb-0"
                />
            </div>

            <!-- Content type filters -->
            <div
                v-if="filters.length > 1"
                class="w-full mb-6"
                :class="filters.length > 3 ? 'max-w-lg' : 'max-w-sm'"
            >
                <button-bar
                    :options="contentTypeOptions"
                    :selected-option-name="activeFilter"
                    @select="setContentTypeFilter"
                    :responsive="true"
                    size="xs"
                    class="!mb-0"
                />
            </div>
        </div>

        <!-- Empty state -->
        <div v-if="filteredPieces.length === 0" class="text-center py-16">
            <div class="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-brandPrimary-100">
                <ai-agent-content-creator-avatar class="w-full h-full" />
            </div>
            <h3 class="text-lg font-semibold text-gray-800 mb-1">
                {{ _mft('content-creator:list.emptyTitle') }}
            </h3>
            <p class="text-sm text-gray-500 mb-4">
                {{ _mft('content-creator:list.emptyDescription') }}
            </p>
            <a
                :href="createUrl"
            >
                <button-primary>
                    {{ _mft('content-creator:list.createFirst') }}
                </button-primary>
            </a>
        </div>

        <!-- Content grid -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div
                v-for="piece in filteredPieces"
                :key="piece.id"
                class="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow group flex flex-col justify-between"
            >
                <img v-if="piece.image_url" :src="piece.image_url"
                     class="w-full h-32 object-cover rounded-t-lg" alt="" />
                <a
                    :href="`${editorBaseUrl}/${piece.id}/editor`"
                    class="block p-4"
                >
                    <div class="flex items-start justify-between mb-2">
                        <span
                            class="px-2 py-0.5 rounded-full text-xs font-medium"
                            :class="contentTypeColors[piece.content_type] || 'bg-gray-100 text-gray-700'"
                        >
                            {{ contentTypeLabels[piece.content_type] || piece.content_type }}
                        </span>
                        <span
                            class="px-2 py-0.5 rounded-full text-xs font-medium"
                            :class="(piece.status || 'draft') === 'published'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'"
                        >
                            {{ (piece.status || 'draft') === 'published' ? _mft('content-creator:list.statusPublished') : _mft('content-creator:list.statusDraft') }}
                        </span>
                    </div>
                    <h3 class="text-sm font-semibold text-gray-800 mb-1 group-hover:text-gray-600 transition-colors">
                        {{ piece.title || _mft('content-creator:list.untitled') }}
                    </h3>
                    <p class="text-xs text-gray-500 line-clamp-3">{{ truncate(piece.content) }}</p>
                </a>
                <div class="flex items-center justify-between px-4 py-2 border-t border-gray-100">
                    <span class="text-xs text-gray-400">{{ formatDate(piece.updated_at) }}</span>
                    <div class="flex items-center gap-1">
                        <button-default
                            @click="toggleStatus(piece)"
                            size="xs"
                            class="opacity-0 group-hover:opacity-100"
                        >
                            {{ (piece.status || 'draft') === 'published' ? _mft('content-creator:list.markDraft') : _mft('content-creator:list.markPublished') }}
                        </button-default>
                        <button-default
                            @click="deletePiece(piece)"
                            size="xs"
                            class="opacity-0 group-hover:opacity-100 !px-1.5 !py-1 !text-red-500"
                        >
                            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                        </button-default>
                    </div>
                </div>
            </div>
        </div>

        <!-- Historical Posts Modal -->
        <ai-content-creator-historical-posts
            v-if="showHistoricalPosts"
            :posts-url="historicalPostsUrl"
            :store-url="historicalPostsStoreUrl"
            @close="showHistoricalPosts = false"
        ></ai-content-creator-historical-posts>
    </div>
</template>
